import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { kopsAPI } from "./kitchenApi";
import { toast } from "sonner";
import { Trash2, Plus, Camera, Filter, Upload, X, FileText, Loader2 } from "lucide-react";
import { cls, Fld, Modal, Footer } from "./KitchenPantry";
import { DatePicker } from "@/components/ui/date-picker";
import { NumberInput } from "@/components/ui/number-input";

const REASONS = [
  { k: "waste",      label: "Waste" },
  { k: "spoilage",   label: "Spoilage" },
  { k: "burnt",      label: "Burnt" },
  { k: "expired",    label: "Expired" },
  { k: "breakage",   label: "Breakage" },
  { k: "staff_meal", label: "Staff Meal" },
  { k: "returned",   label: "Returned" },
  { k: "sampling",   label: "Sampling" },
];
const REASON_TONE = {
  waste:      "bg-slate-100 text-slate-700",
  spoilage:   "bg-amber-100 text-amber-700",
  burnt:      "bg-rose-100 text-rose-700",
  expired:    "bg-rose-100 text-rose-700",
  breakage:   "bg-orange-100 text-orange-700",
  staff_meal: "bg-emerald-100 text-emerald-700",
  returned:   "bg-sky-100 text-sky-700",
  sampling:   "bg-violet-100 text-violet-700",
};

export default function WasteManagement() {
  const [rows, setRows] = useState([]);
  const [pantry, setPantry] = useState([]);
  const [reason, setReason] = useState("");
  const [creating, setCreating] = useState(false);
  const [sp, setSp] = useSearchParams();

  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);

  const load = () => kopsAPI.waste({ business_date: date, limit: 300 }).then(setRows).catch(()=>{});
  useEffect(() => { load(); }, [date]);  // eslint-disable-line
  useEffect(() => { kopsAPI.pantry().then(setPantry); }, []);
  useEffect(() => {
    if (sp.get("new") === "1") { setCreating(true); sp.delete("new"); setSp(sp, { replace: true }); }
  }, [sp, setSp]);

  const summary = useMemo(() => {
    const m = {}; rows.forEach(w => { m[w.reason] = (m[w.reason]||0) + 1; });
    return m;
  }, [rows]);
  const filtered = useMemo(() =>
    reason ? rows.filter(w => w.reason === reason) : rows,
  [rows, reason]);

  return (
    <div className="space-y-4" data-testid="kops-waste">
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400"/>
          <DatePicker value={(date || '').slice(0,10)} onChange={(_v) => setDate(_v)} data-testid="kops-waste-date"/>
        </div>
        <div className="flex gap-1 overflow-x-auto" data-testid="kops-waste-reason-filter">
          <button onClick={()=>setReason("")} data-testid="kops-waste-filter-all"
                  className={`h-9 px-3 rounded-lg text-[12.5px] font-medium border ${
                    !reason ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            All ({rows.length})
          </button>
          {REASONS.map(r => (
            <button key={r.k} onClick={()=>setReason(r.k)} data-testid={`kops-waste-filter-${r.k}`}
                    className={`h-9 px-3 rounded-lg text-[12.5px] font-medium border whitespace-nowrap ${
                      reason===r.k ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {r.label}{summary[r.k] ? ` (${summary[r.k]})` : ""}
            </button>
          ))}
        </div>
        <button onClick={()=>setCreating(true)} data-testid="kops-waste-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-95">
          <Plus className="w-4 h-4"/> Log Waste
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Trash2 className="w-12 h-12 mx-auto text-slate-300 mb-2"/>
            <div className="text-[13px] text-slate-500">No waste logged for this filter.</div>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Ingredient</th>
                <th className="text-right px-4 py-3">Quantity</th>
                <th className="text-center px-4 py-3">Reason</th>
                <th className="text-left px-4 py-3">Employee</th>
                <th className="text-left px-4 py-3">Timestamp</th>
                <th className="text-left px-4 py-3">Notes</th>
                <th className="text-center px-4 py-3">Photo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(w => (
                <tr key={w.id} data-testid={`kops-waste-row-${w.id}`} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-medium text-hocp-navy">{w.item_name}</td>
                  <td className="px-4 py-3 text-right mono tabular-nums font-semibold">{w.qty} <span className="text-[11px] text-slate-400 uppercase">{w.unit}</span></td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium capitalize ${REASON_TONE[w.reason] || "bg-slate-100 text-slate-700"}`}>
                      {(w.reason || "").replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-slate-600">{w.actor_email}</td>
                  <td className="px-4 py-3 text-[12px] text-slate-500 mono">{fmtDT(w.at)}</td>
                  <td className="px-4 py-3 text-[12px] text-slate-500 truncate max-w-xs italic">{w.notes || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    {w.photo_url
                      ? <a href={w.photo_url} target="_blank" rel="noreferrer" className="text-hocp-navy hover:underline"><Camera className="w-4 h-4 inline"/></a>
                      : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && <WasteDialog pantry={pantry}
                                 onClose={()=>setCreating(false)}
                                 onDone={()=>{ setCreating(false); load(); }}/>}
    </div>
  );
}

function WasteDialog({ pantry, onClose, onDone }) {
  const [f, setF] = useState({
    pantry_item_id: "", item_name: "", qty: "", unit: "kg",
    reason: "waste", notes: "", photo_url: "",
  });
  const [saving, setSaving] = useState(false);

  const pickItem = (id) => {
    const p = pantry.find(x => x.id === id);
    if (!p) return;
    setF({ ...f, pantry_item_id: id, item_name: p.item_name, unit: p.unit });
  };

  const submit = async () => {
    if (!f.pantry_item_id) { toast.error("Pick an ingredient"); return; }
    if (!Number(f.qty) || Number(f.qty) <= 0) { toast.error("Quantity must be > 0"); return; }
    setSaving(true);
    try {
      await kopsAPI.createWaste({
        pantry_item_id: f.pantry_item_id,
        item_name:      f.item_name,
        qty:            String(f.qty),
        unit:           f.unit,
        reason:         f.reason,
        notes:          f.notes || null,
        photo_url:      f.photo_url || null,
      });
      toast.success("Waste logged — inventory adjusted");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const picked = pantry.find(x => x.id === f.pantry_item_id);

  return (
    <Modal title="Log Kitchen Waste" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Ingredient" full>
          <select value={f.pantry_item_id} onChange={e=>pickItem(e.target.value)}
                  data-testid="kops-waste-item" className={cls}>
            <option value="">Select ingredient…</option>
            {pantry.map(p => <option key={p.id} value={p.id}>{p.item_name} — {p.current_stock} {p.unit} on hand</option>)}
          </select>
          {picked && (
            <div className="text-[11px] text-slate-500 mt-1 mono">
              Available: <span className="font-semibold">{picked.current_stock} {picked.unit}</span>
              {" · "}Min: {picked.min_stock} {picked.unit}
            </div>
          )}
        </Fld>
        <Fld label="Quantity">
          <NumberInput step="0.001" value={f.qty} onChange={e=>setF({...f, qty: e.target.value})}
                 data-testid="kops-waste-qty" className={cls}/>
        </Fld>
        <Fld label="Unit"><input value={f.unit} onChange={e=>setF({...f, unit: e.target.value})} data-testid="kops-waste-unit" className={cls}/></Fld>
        <Fld label="Reason" full>
          <div className="grid grid-cols-4 gap-2">
            {REASONS.map(r => (
              <button key={r.k} type="button" onClick={()=>setF({...f, reason: r.k})}
                      data-testid={`kops-waste-reason-${r.k}`}
                      className={`h-10 px-2 rounded-lg border text-[12px] font-medium capitalize transition ${
                        f.reason===r.k
                          ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                {r.label}
              </button>
            ))}
          </div>
        </Fld>
        <Fld label="Notes" full>
          <textarea value={f.notes} onChange={e=>setF({...f, notes: e.target.value})}
                    data-testid="kops-waste-notes" rows={2}
                    className={cls + " h-auto py-2 resize-y"}
                    placeholder="Optional — details, batch reference, corrective action, etc."/>
        </Fld>
        <Fld label="Photo (optional)" full>
          <PhotoUploader
            value={f.photo_url}
            onChange={(url) => setF({ ...f, photo_url: url })}
          />
        </Fld>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="kops-waste-submit"/>
    </Modal>
  );
}

function fmtDT(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString([], { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }); }
  catch { return "—"; }
}

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,application/pdf";
const MAX_MB = 10;

function PhotoUploader({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [meta, setMeta] = useState(null);  // { filename, content_type } for the current value

  const isPdf = (meta?.content_type === "application/pdf") ||
                 (typeof value === "string" && value.toLowerCase().endsWith(".pdf"));

  const pickFile = () => inputRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type)) {
      toast.error("Only images (JPG, PNG, WebP, GIF) or PDF are allowed");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_MB} MB limit`);
      return;
    }
    setUploading(true);
    try {
      const res = await kopsAPI.uploadPhoto(file);
      onChange(res.url);
      setMeta({ filename: res.filename, content_type: res.content_type });
      toast.success("Photo attached");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const clear = () => { onChange(""); setMeta(null); if (inputRef.current) inputRef.current.value = ""; };

  if (value) {
    return (
      <div
        data-testid="kops-waste-photo-preview"
        className="relative rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-center gap-3"
      >
        {isPdf ? (
          <div className="w-16 h-16 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500">
            <FileText className="w-7 h-7" />
          </div>
        ) : (
          <img
            src={value}
            alt="Waste evidence"
            className="w-16 h-16 rounded-lg object-cover border border-slate-200 bg-white"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-medium text-slate-700 truncate">
            {meta?.filename || (isPdf ? "Document" : "Photo attached")}
          </div>
          <div className="text-[11px] text-slate-400 mono truncate">{value}</div>
        </div>
        <button
          type="button"
          onClick={clear}
          data-testid="kops-waste-photo-remove"
          className="h-8 px-2 rounded-lg text-slate-500 hover:bg-slate-200 inline-flex items-center gap-1 text-[12px]"
        >
          <X className="w-4 h-4" /> Remove
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="kops-waste-photo-dropzone"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={pickFile}
      className={`cursor-pointer rounded-xl border-2 border-dashed transition p-6 text-center ${
        dragOver
          ? "border-hocp-navy bg-hocp-navy/5"
          : "border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid="kops-waste-photo-input"
      />
      {uploading ? (
        <div className="inline-flex items-center gap-2 text-[13px] text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
        </div>
      ) : (
        <>
          <Upload className="w-6 h-6 mx-auto text-slate-400 mb-1.5" />
          <div className="text-[13px] font-medium text-slate-700">Click to upload or drag & drop</div>
          <div className="text-[11.5px] text-slate-400 mt-0.5">JPG, PNG, WebP, GIF or PDF · up to {MAX_MB} MB</div>
        </>
      )}
    </div>
  );
}
